import { useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  History, Search, Loader2, RotateCcw, Eye, Layers, Clock, Cpu, AlertTriangle,
  CheckCircle2, ScanEye, Split, ShieldCheck, Wand2, Scissors, Undo2,
  Siren, RefreshCw, PlayCircle,
} from 'lucide-react';

const fmt = (t) => (t ? new Date(t).toLocaleString() : '—');

function StageChip({ s }) {
  const warn = (s.warnings || []).length > 0;
  return (
    <div
      className={`border rounded-md px-3 py-2 text-xs ${warn ? 'border-amber-300 bg-amber-50' : 'border-border bg-card'}`}
      data-testid={`parserlog-stage-${s.stage}`}
    >
      <div className="flex items-center gap-2 font-semibold">
        <Cpu className="h-3.5 w-3.5" />
        {s.stage}
        {s.ms != null && <span className="text-muted-foreground">· {s.ms}ms</span>}
        {s.model && <Badge variant="outline" className="text-[10px]">{s.model}</Badge>}
        {s.llm_calls != null && <span className="text-muted-foreground">· {s.llm_calls} LLM calls</span>}
      </div>
      {(s.corrections || []).length > 0 && (
        <div className="mt-1 text-emerald-700">
          {(s.corrections || []).length} correction(s)
        </div>
      )}
      {(s.warnings || []).length > 0 && (
        <div className="mt-1 text-amber-700 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {(s.warnings || []).join(', ')}
        </div>
      )}
      {s.manifest && (
        <div className="mt-1 text-muted-foreground">
          OCR pages {s.manifest.ocr_pages_count}/{s.manifest.pdf_page_count ?? '?'}
          {s.manifest.complete === false && (
            <span className="text-rose-700"> · missing {JSON.stringify(s.manifest.missing_pages)}</span>
          )}
          {s.manifest.complete === true && <span className="text-emerald-700"> · complete</span>}
        </div>
      )}
    </div>
  );
}

export default function ParserLogsPage() {
  const [docId, setDocId] = useState('');
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState(null);
  const [versions, setVersions] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [visionLoading, setVisionLoading] = useState(false);
  const [vision, setVision] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Stuck-job watchdog panel state
  const [stuck, setStuck] = useState(null);
  const [stuckLoading, setStuckLoading] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);

  const loadStuck = useCallback(async () => {
    setStuckLoading(true);
    try {
      const { data } = await api.get('/super-admin/parser-v2/stuck-jobs', { params: { threshold_min: 45 } });
      setStuck(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load stuck jobs');
    } finally {
      setStuckLoading(false);
    }
  }, []);

  const runSweep = async (dry) => {
    if (!dry && !window.confirm('Requeue (bounded) or fail-out every stuck parse job and drain the dead-letter queue?')) return;
    setSweeping(true); setSweepResult(null);
    try {
      const { data } = await api.post('/super-admin/parser-v2/stuck-jobs/sweep', null, {
        params: { dry_run: dry, threshold_min: 45, max_retries: 1, drain_dlq: true },
      });
      setSweepResult(data);
      const rq = (data.requeued || []).length, fo = (data.failed_out || []).length;
      toast.success(dry
        ? `Dry run: would requeue ${rq}, fail-out ${fo} (scanned ${data.scanned})`
        : `Swept: requeued ${rq}, failed-out ${fo}, DLQ drained ${data.dlq_drained}`);
      await loadStuck();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  const load = useCallback(async () => {
    const id = docId.trim();
    if (!id) { toast.error('Enter a document ID'); return; }
    setLoading(true); setVision(null);
    try {
      const [lg, vs] = await Promise.all([
        api.get(`/super-admin/parser-v2/documents/${id}/parse-log`, { params: { run: 'all' } }),
        api.get(`/super-admin/parser-v2/documents/${id}/versions`),
      ]);
      setRuns(lg.data.runs || []);
      setVersions(vs.data.versions || []);
      if (!(lg.data.runs || []).length && !(vs.data.versions || []).length) {
        toast.message('No parse logs or versions for this document yet.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load parser logs');
    } finally {
      setLoading(false);
    }
  }, [docId]);

  const restore = async (n) => {
    if (!window.confirm(`Restore version ${n}? The current data is snapshotted first, then validation re-runs.`)) return;
    setRestoring(n);
    try {
      const { data } = await api.post(`/super-admin/parser-v2/documents/${docId.trim()}/versions/${n}/restore`);
      toast.success(`Restored version ${n} · revalidated (${data?.validation?.type || 'done'})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  const runVision = async () => {
    setVisionLoading(true);
    try {
      const { data } = await api.post(`/super-admin/parser-v2/documents/${docId.trim()}/vision-anchor`);
      await load();
      setVision(data);
      toast.success('Vision anchor complete (shadow — extracted data unchanged)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Vision anchor failed');
    } finally {
      setVisionLoading(false);
    }
  };

  const runAction = async (key, path, okMsg) => {
    if (!docId.trim()) { toast.error('Enter a document ID'); return; }
    setActionLoading(key);
    try {
      const { data } = await api.post(`/super-admin/parser-v2/documents/${docId.trim()}${path}`);
      toast.success(okMsg(data));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="parser-logs-page">
      <div>
        <h2 className="text-xl font-black tracking-tight flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          <History className="h-5 w-5" /> Parser Logs & Versions
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Inspect the parse-run stage timeline, snapshot history, and restore a prior extraction (Parser V2 · Super Admin).
        </p>
      </div>

      {/* Stuck-job watchdog */}
      <div className="border rounded-lg p-4 bg-card" data-testid="stuck-jobs-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-semibold flex items-center gap-2">
            <Siren className="h-4 w-4 text-rose-600" /> Stuck-job Watchdog
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={loadStuck} disabled={stuckLoading} data-testid="stuck-refresh-btn">
              {stuckLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1.5">Refresh</span>
            </Button>
            <Button size="sm" variant="secondary" onClick={() => runSweep(true)} disabled={sweeping} data-testid="stuck-dryrun-btn">
              {sweeping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
              <span className="ml-1.5">Dry run</span>
            </Button>
            <Button size="sm" onClick={() => runSweep(false)} disabled={sweeping} data-testid="stuck-sweep-btn">
              {sweeping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
              <span className="ml-1.5">Sweep now</span>
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Recovers documents left in <span className="font-mono">processing</span> after a Lambda timeout/crash — requeues once, then marks failed with a clear message. Threshold 45 min.
        </p>

        {stuck && (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap gap-4 text-sm" data-testid="stuck-queue-health">
              <span>Stuck (&gt;45m): <b className={stuck.stuck_count ? 'text-rose-700' : 'text-emerald-700'}>{stuck.stuck_count}</b></span>
              <span className="text-muted-foreground">Queue available: <b>{stuck.queue?.main?.available ?? '—'}</b></span>
              <span className="text-muted-foreground">In-flight: <b>{stuck.queue?.main?.in_flight ?? '—'}</b></span>
              <span className="text-muted-foreground">DLQ: <b className={stuck.queue?.dlq?.available ? 'text-amber-700' : ''}>{stuck.queue?.dlq?.available ?? '—'}</b></span>
            </div>
            {(stuck.stuck || []).length > 0 ? (
              <div className="space-y-1">
                {stuck.stuck.map((d) => (
                  <div key={d.id} className="text-xs font-mono flex flex-wrap gap-2 items-center border-b border-border/50 py-1" data-testid={`stuck-row-${d.id}`}>
                    <span className="text-rose-700 font-bold">{d._stuck_minutes ?? '?'}m</span>
                    <span className="text-muted-foreground">{d.id}</span>
                    <span>{d.filename}</span>
                    {d.watchdog_retry_count ? <Badge variant="outline" className="text-[10px]">retry {d.watchdog_retry_count}</Badge> : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> No stuck jobs.</div>
            )}
            {sweepResult && (
              <div className="text-xs border rounded-md p-2 bg-muted/40" data-testid="stuck-sweep-result">
                {sweepResult.dry_run ? 'DRY RUN — ' : ''}scanned {sweepResult.scanned} · requeued {(sweepResult.requeued || []).length} · failed-out {(sweepResult.failed_out || []).length} · DLQ drained {sweepResult.dlq_drained}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 max-w-2xl">
        <Input
          value={docId}
          onChange={(e) => setDocId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="Document ID"
          data-testid="parserlog-docid-input"
        />
        <Button onClick={load} disabled={loading} data-testid="parserlog-load-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          <span className="ml-2">Load</span>
        </Button>
        <Button variant="outline" onClick={runVision} disabled={visionLoading || !docId.trim()} data-testid="parserlog-vision-btn">
          {visionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanEye className="h-4 w-4" />}
          <span className="ml-2">Run Vision Anchor</span>
        </Button>
      </div>

      {/* Guarded healing actions (Parser V2 Phase 4/5 + vision promote) */}
      <div className="flex flex-wrap items-center gap-2 max-w-3xl">
        <Button size="sm" variant="secondary" disabled={!!actionLoading || !docId.trim()}
          onClick={() => runAction('subtype', '/detect-subtype', (d) => `Subtype: ${d.subtype} (${d.logical_bill_count} logical bill${d.logical_bill_count === 1 ? '' : 's'})`)}
          data-testid="parserlog-detect-subtype-btn">
          {actionLoading === 'subtype' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Split className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Detect Subtype</span>
        </Button>
        <Button size="sm" variant="secondary" disabled={!!actionLoading || !docId.trim()}
          onClick={() => runAction('guards', '/apply-statement-guards', (d) => d.applied ? `Applied ${d.applied} statement fix(es)` : 'No misallocation found')}
          data-testid="parserlog-statement-guards-btn">
          {actionLoading === 'guards' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Apply Statement Guards</span>
        </Button>
        <Button size="sm" variant="secondary" disabled={!!actionLoading || !docId.trim()}
          onClick={() => runAction('applyvision', '/apply-vision', (d) => d.applied ? `Promoted ${d.applied} vision fix(es)` : (d.reason || 'Nothing to apply'))}
          data-testid="parserlog-apply-vision-btn">
          {actionLoading === 'applyvision' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Promote Vision Fixes</span>
        </Button>
        <Button size="sm" variant="secondary" disabled={!!actionLoading || !docId.trim()}
          onClick={() => runAction('split', '/split-into-singles', (d) => `Split into ${d.count} single bill(s) — see the Bills → Single tab`)}
          data-testid="parserlog-split-singles-btn">
          {actionLoading === 'split' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scissors className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Split into Singles</span>
        </Button>
        <Button size="sm" variant="ghost" disabled={!!actionLoading || !docId.trim()}
          onClick={() => runAction('unsplit', '/unsplit', (d) => `Reverted — removed ${d.removed_logical_bills} logical bill(s)`)}
          data-testid="parserlog-unsplit-btn">
          {actionLoading === 'unsplit' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Un-split</span>
        </Button>
      </div>

      {vision && (
        <div className="border rounded-lg p-4 bg-indigo-50 border-indigo-200" data-testid="parserlog-vision-result">
          <div className="font-semibold flex items-center gap-2 text-indigo-900">
            <ScanEye className="h-4 w-4" /> Vision Anchor (shadow)
            <Badge variant="outline">{vision?.ai?.model}</Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
            <div><div className="text-xs text-muted-foreground">Master account</div><div className="font-mono font-bold" data-testid="parserlog-vision-master">{vision?.anchor?.master_account_no || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Sub-accounts</div><div className="font-bold">{vision?.vision_subaccount_count ?? '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Roster rows</div><div className="font-bold">{vision?.roster_count ?? 0}</div></div>
            <div><div className="text-xs text-muted-foreground">Anchor pages</div><div className="font-bold">{JSON.stringify(vision?.anchor_pages || [])}</div></div>
          </div>
          {(vision?.reconciliation || []).length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Reconciliation</div>
              <div className="space-y-1">
                {vision.reconciliation.map((r, i) => (
                  <div key={i} className="text-xs font-mono flex flex-wrap gap-2 items-center" data-testid={`parserlog-recon-${i}`}>
                    <span className="text-muted-foreground">{r.field}</span>
                    <span>text=<b>{String(r.text_val)}</b></span>
                    <span>vision=<b>{String(r.vision_val)}</b></span>
                    <span className="text-emerald-700">→ {String(r.chosen)}</span>
                    <Badge variant="outline" className="text-[10px]">{r.reason}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {runs && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Parse-run timeline */}
          <div>
            <h3 className="font-semibold flex items-center gap-2 mb-2"><Clock className="h-4 w-4" /> Parse runs ({runs.length})</h3>
            {runs.length === 0 && <div className="text-sm text-muted-foreground">No parse runs recorded.</div>}
            <div className="space-y-3">
              {runs.map((r) => (
                <div key={r.parse_run_id} className="border rounded-lg p-3 bg-card" data-testid={`parserlog-run-${r.parse_run_id}`}>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="font-mono text-muted-foreground">{r.parse_run_id}</span>
                    <span className="text-muted-foreground">{fmt(r.created_at)}</span>
                  </div>
                  <div className="space-y-2">
                    {(r.stages || []).map((s, i) => <StageChip key={i} s={s} />)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Versions + restore */}
          <div>
            <h3 className="font-semibold flex items-center gap-2 mb-2"><Layers className="h-4 w-4" /> Version snapshots ({(versions || []).length})</h3>
            {(versions || []).length === 0 && <div className="text-sm text-muted-foreground">No snapshots yet.</div>}
            <div className="space-y-2">
              {(versions || []).map((v) => (
                <div key={v.version_no} className="border rounded-lg p-3 bg-card flex items-center justify-between" data-testid={`parserlog-version-${v.version_no}`}>
                  <div className="text-sm">
                    <div className="font-bold flex items-center gap-2">
                      v{v.version_no}
                      <Badge variant="outline" className="text-[10px]">{v.source}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">{fmt(v.created_at)} · {v.created_by}</div>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => restore(v.version_no)}
                    disabled={restoring === v.version_no}
                    data-testid={`parserlog-restore-${v.version_no}`}
                  >
                    {restoring === v.version_no ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                    <span className="ml-1.5">Restore</span>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
